/**
 * File Operation Error Helpers
 *
 * Utility functions for extracting user-friendly error messages
 * from API errors during file operations (move, copy, delete, etc.).
 */

type FileOperation = 'move' | 'copy' | 'delete' | 'rename' | 'upload';

export interface FileOperationError {
  message: string;
  isFileLocked: boolean;
}

/**
 * Type guard for errors with a response.status property.
 * Uses duck typing to avoid instanceof issues with bundling.
 */
function hasResponseStatus(
  error: unknown
): error is { response: { status: number } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response: unknown }).response === 'object' &&
    (error as { response: unknown }).response !== null &&
    'status' in (error as { response: { status: unknown } }).response &&
    typeof (error as { response: { status: unknown } }).response.status === 'number'
  );
}

/**
 * Get a user-friendly error for file operation errors.
 *
 * Handles specific error codes:
 * - 423 Locked: File is in use by another user/process
 * - 409 Conflict: File already exists or naming conflict
 * - Generic: Falls back to "Failed to {operation}" message
 *
 * @param error - The error that occurred
 * @param operation - The file operation that was attempted
 * @returns An object with the error message and whether the file is locked
 */
export function getFileOperationError(
  error: unknown,
  operation: FileOperation
): FileOperationError {
  if (hasResponseStatus(error)) {
    // Check for 423 Locked - File is in use
    if (error.response.status === 423) {
      return {
        message: `The file is in use and cannot be ${operation === 'copy' ? 'copied' : operation + 'd'}. Please try again later.`,
        isFileLocked: true,
      };
    }

    // Check for 409 Conflict - File already exists or naming conflict
    if (error.response.status === 409) {
      return {
        message: `A file with the same name already exists in the destination folder.`,
        isFileLocked: false,
      };
    }
  }

  // Default message for other errors
  return {
    message: `Failed to ${operation} the item`,
    isFileLocked: false,
  };
}

/**
 * Get a user-friendly error message for file operation errors.
 * @deprecated Use getFileOperationError instead for more control over error display.
 */
export function getFileOperationErrorMessage(
  error: unknown,
  operation: FileOperation
): string {
  return getFileOperationError(error, operation).message;
}
